import React from "react";
import { Avatar, AvatarProps } from "@UI/Components/Avatar/Avatar";
import { Box } from "@UI/Components/Box/Box";
import { Icon } from "@UI/Components/Icon/Icon";
import { Link } from "@UI/Components/Link/Link";
import {
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
} from "@UI/Components/Popover/Popover";
import { Portal } from "@UI/Components/Portal/Portal";
import { VStack } from "@UI/Components/Stack/Stack";
import { Text } from "@UI/Components/Text/Text";
import { ICandidate } from "@modules/ServiceRequest/types";
import { SuperTalentBadgeWithWhiteBorder } from "@UI/Components/Icons/SuperTalentBadge";
import { VerifiedBadge } from "@UI/Components/Icons/VerifiedBadge";
import { useFeatureFlag } from "@Libs/FeatureFlags/hooks/useFeatureFlag";
import useThemeToken from "@UI/hooks/useThemeToken";
import { FormattedMessage } from "@I18n/Components/FormattedMessage/FormattedMessage";
import useI18n from "@I18n/hooks/useI18n";
import messages from "../messages";

const sizes = {
  avatar: { s: "50px", md: "74px", lg: "150px" },
  icon: { s: "20px", md: "20px", lg: "40px" },
};

const GRADIENT_BORDER_WIDTH = 4;

export const CandidateAvatar = (
  props: {
    url: string;
    size?: "s" | "md" | "lg";
    isSuperTalent: ICandidate["superTalent"];
    isActivationRateEnabled?: boolean;
    isVerified?: boolean;
  } & AvatarProps
) => {
  const { url, size = "md", isSuperTalent, isActivationRateEnabled, isVerified, ...rest } = props;
  const pink500 = useThemeToken("colors", "pink.500");
  const orange500 = useThemeToken("colors", "orange.500");
  const { formatMessage } = useI18n();

  const {
    isEnabled: isUpfrontVerificationEnabled,
  } = useFeatureFlag("sp_upfront_verification");

  const showGradientBorder = isSuperTalent && isActivationRateEnabled;

  const superTalentIcon = (
    <Box
      position="absolute"
      bottom="0"
      right="0"
      w={sizes.icon[size]}
      h={sizes.icon[size]}
    >
      <Icon as={SuperTalentBadgeWithWhiteBorder} w="100%" h="100%" />
    </Box>
  );

  const avatar = (
    <Avatar
      w={sizes.avatar[size]}
      h={sizes.avatar[size]}
      src={url}
      position="relative"
      {...(showGradientBorder ? {} : rest)}
    >
      {isSuperTalent && (
        showGradientBorder ? (
          <Popover trigger="hover">
            <PopoverTrigger>{superTalentIcon}</PopoverTrigger>
            <Portal selector="body">
              <PopoverContent maxW="190px" bg="gray.800">
                <PopoverArrow bg="gray.800" />
                <PopoverBody>
                  <VStack
                    justifyContent="center"
                    maxW="190px"
                    spacing="0"
                    color="white"
                    fontSize="sm"
                  >
                    <Text textAlign="center" m="0">
                      <FormattedMessage
                        {...messages[
                          "serviceRequest.superTalentBadge.popoverDescription"
                        ]}
                      />
                    </Text>
                    <Link
                      h="fit-content"
                      textDecoration="underline"
                      target="_blank"
                      href={formatMessage(
                        messages["serviceRequest.superTalentBadge.popoverLink"]
                      )}
                    >
                      <Text m="0" display="inline">
                        <FormattedMessage
                          {...messages[
                            "serviceRequest.superTalentBadge.popoverLinkCTA"
                          ]}
                        />
                      </Text>
                    </Link>
                  </VStack>
                </PopoverBody>
              </PopoverContent>
            </Portal>
          </Popover>
        ) : (
          superTalentIcon
        )
      )}

      {isVerified && !isSuperTalent && isUpfrontVerificationEnabled && (
        <Icon
          as={VerifiedBadge}
          position="absolute"
          bottom="0"
          right="0"
          w={sizes.icon[size]}
          h={sizes.icon[size]}
        />
      )}
    </Avatar>
  );

  if (showGradientBorder) {
    return (
      <Box
        borderRadius="50%"
        background={`linear-gradient(96.51deg, ${pink500} 0%, ${orange500} 99.87%)`}
        p={`${GRADIENT_BORDER_WIDTH}px`}
        flexShrink={0}
        {...rest}
      >
        {avatar}
      </Box>
    );
  }

  return avatar;
};
